/**
 * voiceInput — speak a chat message instead of typing it.
 *
 * **Why this exists rather than relying on Safari's own dictation.** The keyboard
 * mic (iOS) and Fn-Fn (macOS) are indistinguishable from typing at the page
 * level: they fire ordinary `input` events, character by character, with no
 * "dictation started/ended" signal. So a message can be dictated but never
 * SUBMITTED — the user still has to reach for the keyboard, which defeats the
 * point when the whole reason to speak is that your hands are busy.
 *
 * The only way to auto-submit from native dictation would be a heuristic like
 * "send after N ms of no input" — which would also fire when someone pauses
 * mid-sentence while TYPING, sending half-written messages. Wrong trade in a
 * panel people also type into.
 *
 * The Web Speech API gives us the lifecycle dictation withholds: `onresult` with
 * `isFinal`, and `onend`. We own the session, so we know when an utterance is
 * complete and can submit deliberately.
 *
 * Supported in Safari as `webkitSpeechRecognition` (macOS 14.1+, iOS 14.5+),
 * Chrome and Edge. Firefox does not implement it — `isSupported()` is false
 * there and the mic button simply doesn't render.
 */

/** Recognition ctor for this browser, or null. Safari/Chrome use the prefix. */
export function recognitionCtor(win) {
  const w = win || (typeof window !== 'undefined' ? window : undefined);
  if (!w) return null;
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export function isSupported(win) {
  return recognitionCtor(win) !== null;
}

/**
 * How long a finished transcript sits on screen before it sends.
 *
 * There IS a cancel window rather than sending instantly because these drive
 * hardware: "turn the screen off" and "turn the screen on" differ by one
 * phoneme, and a misrecognition changes a wall display. Two seconds is long
 * enough to read a short command and hit ✕, short enough that hands-free still
 * feels hands-free.
 */
export const SEND_DELAY_MS = 2000;

/**
 * In a SHARED room a plain message goes to the room and `@aveq …` goes to the
 * assistant — and voice follows the same rule as typing. A spoken message is
 * room chat unless the speaker ADDRESSED the assistant ("at aveq, turn the
 * screen off"); recognizers render that mention a few ways, so this normalises
 * them into the one token the room understands and never double-prefixes.
 *
 * voice-room-no-auto-mention slug: this used to prefix `@aveq` onto EVERY
 * utterance in a room, so a person talking to the room by voice was instead
 * commanding the assistant. The mention is now opt-in by saying it, exactly as
 * it is when typing.
 */
export function withMention(text, isRoom) {
  const t = (text || '').trim();
  if (!t || !isRoom) return t;
  if (/^@?\s*aveq\b/i.test(t) || /^at\s+aveq\b/i.test(t)) {
    // Normalise a spoken "at aveq" / bare "aveq" into the real mention token.
    return t.replace(/^(@?\s*aveq|at\s+aveq)\b[,:]?\s*/i, '@aveq ').trim();
  }
  return t;
}

/** Human-readable reason for a recognition error event. */
export function describeError(err) {
  switch (err) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'Microphone access was blocked. Allow it in your browser settings to use voice.';
    case 'no-speech':
      // Silence, which in voice MODE is just a pause between commands — the
      // mic is still on and there is nothing for the user to do. Returns null
      // for the same reason 'aborted' does: a caller should not have to
      // remember which "quiet" errors also need filtering at the call site.
      return null;
    case 'audio-capture':
      return 'No microphone found.';
    case 'network':
      return 'Speech recognition needs a network connection.';
    case 'aborted':
      return null;          // user-initiated; not worth a message
    default:
      return 'Voice input failed. Tap the mic to try again.';
  }
}

/**
 * Start one listening session. Returns a handle with `stop()` / `abort()`, or
 * null when the browser can't do it.
 *
 * Deliberately SINGLE-UTTERANCE (`continuous = false`): Safari's continuous mode
 * is unreliable, and one spoken command per tap is the interaction we want
 * anyway. The recognizer stops itself at the end of an utterance.
 *
 * `ctor` is injectable so the logic is testable without a browser.
 */
export function startSession({ ctor, lang, onInterim, onFinal, onError, onEnd } = {}) {
  const Ctor = ctor || recognitionCtor();
  if (!Ctor) return null;

  const rec = new Ctor();
  rec.continuous = false;
  rec.interimResults = true;      // show words as they land, so it feels alive
  rec.maxAlternatives = 1;
  rec.lang = lang || (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

  let finalText = '';

  rec.onresult = (event) => {
    let interim = '';
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const r = event.results[i];
      const text = (r[0] && r[0].transcript) || '';
      if (r.isFinal) finalText += text;
      else interim += text;
    }
    if (interim && onInterim) onInterim(interim.trim());
    if (finalText && onFinal) onFinal(finalText.trim());
  };

  rec.onerror = (event) => {
    if (onError) onError(event && event.error);
  };

  // Safari can end a session without ever producing a final result (silence, or
  // the user tapping stop mid-utterance). Surface that so the caller can clear
  // its "listening" state instead of spinning forever.
  rec.onend = () => {
    if (onEnd) onEnd(finalText.trim());
  };

  try {
    rec.start();
  } catch (e) {
    // start() throws if a session is already running — treat as a failed start
    // rather than letting it bubble into a render.
    if (onError) onError('already-started');
    return null;
  }

  return {
    stop: () => { try { rec.stop(); } catch (e) { /* already stopped */ } },
    abort: () => { try { rec.abort(); } catch (e) { /* already stopped */ } },
  };
}
